import {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  useCallback,
} from 'react';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { clearAccessToken, getAccessToken, setAccessToken } from '../services/tokenStore';

interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string | null;
  stripeCustomerId?: string;
  emailVerified?: boolean;
  role?: string;
  /**
   * /demo only: the greeting name resolved from the invite's contact name,
   * already run through `personalFirstName` (or the 'Demo Account' fallback).
   * Real accounts never set this — DashboardHome falls back to deriving one
   * from `name` when it's absent.
   */
  displayName?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  resendVerification: () => Promise<boolean>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Enable sending cookies with all requests
axios.defaults.withCredentials = true;

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const interceptorIdRef = useRef<number | null>(null);

  // Function to refresh the access token using the httpOnly cookie
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const response = await axios.post('/api/auth/refresh-token');
      const { token } = response.data.data;

      if (token) {
        setAccessToken(token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return token;
      }
      return null;
    } catch {
      // Refresh failed - clear auth state
      clearAccessToken();
      delete axios.defaults.headers.common['Authorization'];
      return null;
    }
  }, []);

  // Setup axios interceptor for automatic token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        // If error is 401 and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Don't retry for login, register, or refresh-token endpoints
          const isAuthEndpoint =
            originalRequest.url?.includes('/api/auth/login') ||
            originalRequest.url?.includes('/api/auth/register') ||
            originalRequest.url?.includes('/api/auth/refresh-token');

          if (isAuthEndpoint) {
            return Promise.reject(error);
          }

          if (isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(token => {
                if (token) {
                  originalRequest.headers['Authorization'] = `Bearer ${token}`;
                }
                return axios(originalRequest);
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const newToken = await refreshAccessToken();

            if (newToken) {
              processQueue(null, newToken);
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            } else {
              // Refresh failed - user needs to log in again
              processQueue(new Error('Refresh token expired'), null);
              setUser(null);
              return Promise.reject(error);
            }
          } catch (refreshError) {
            processQueue(refreshError as Error, null);
            setUser(null);
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );

    interceptorIdRef.current = interceptor;

    return () => {
      if (interceptorIdRef.current !== null) {
        axios.interceptors.response.eject(interceptorIdRef.current);
      }
    };
  }, [refreshAccessToken]);

  const checkAuth = useCallback(async () => {
    const token = getAccessToken();
    if (token) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get('/api/auth/profile');
        setUser(response.data.data.user);
      } catch {
        // Token might be expired, try to refresh
        const newToken = await refreshAccessToken();
        if (newToken) {
          try {
            const response = await axios.get('/api/auth/profile');
            setUser(response.data.data.user);
          } catch {
            setUser(null);
          }
        } else {
          clearAccessToken();
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        }
      }
    } else {
      // No access token, try to get one using refresh token cookie
      const newToken = await refreshAccessToken();
      if (newToken) {
        try {
          const response = await axios.get('/api/auth/profile');
          setUser(response.data.data.user);
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    }
    setLoading(false);
  }, [refreshAccessToken]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    const response = await axios.post('/api/auth/login', { email, password });
    const { token, user: userData } = response.data.data;

    setAccessToken(token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear the refresh token cookie
      await axios.post('/api/auth/logout');
    } catch {
      // Ignore errors - we'll clear local state anyway
    }
    clearAccessToken();
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const refreshAuth = async () => {
    setLoading(true);
    await checkAuth();
  };

  const resendVerification = async (): Promise<boolean> => {
    if (!user?.email) {
      return false;
    }

    try {
      await axios.post('/api/auth/resend-verification', { email: user.email });
      return true;
    } catch {
      return false;
    }
  };

  return {
    user,
    loading,
    login,
    logout,
    refreshAuth,
    resendVerification,
    isAuthenticated: !!user,
  };
};

export { AuthContext };
